"use client";

import { useState, useEffect, type FC } from "react";
import { Snipe } from "./snipe";
import SnipeStatusTabs from "./SnipeStatusTabs";
import { useMempadTokenInfoForSnipes } from "./useMempadTokenInfoForSnipes";
import { useSnipesStore } from "@/stores/snipes.store";
import { showToast } from "@utils/showToast";
import { useSniper } from "@/stores/sniper.store";
import { cn } from "@/lib/utils";

// Define the expected status types, matching the Snipe component's prop type
type SnipeStatus = "Active" | "Cancelled" | "In Progress" | "Success" | "Failed" | "Rejected" | "Migrated/Unknown";

// Local storage key for deleted snipes
const DELETED_SNIPES_KEY = "deleted_snipes";

// Helper function to get deleted snipes from local storage
const getDeletedSnipes = (): string[] => {
    if (typeof window === "undefined") return [];
    const deletedSnipes = localStorage.getItem(DELETED_SNIPES_KEY);
    return deletedSnipes ? JSON.parse(deletedSnipes) : [];
};

// Helper function to add a snipe to deleted snipes in local storage
const addDeletedSnipe = (guid: string): void => {
    if (typeof window === "undefined") return;
    const deletedSnipes = getDeletedSnipes();
    if (!deletedSnipes.includes(guid)) {
        deletedSnipes.push(guid);
        localStorage.setItem(DELETED_SNIPES_KEY, JSON.stringify(deletedSnipes));
    }
};

const SnipeList: FC<{ twoCol?: boolean; showHeader?: boolean; showMemes?: boolean; className?: string }> = ({
    twoCol = false,
    showHeader = true,
    showMemes = false,
    className,
}) => {
    // Explicitly type the map's values as SnipeStatus
    const sniperStatusMap: { [key: number]: SnipeStatus } = {
        0: "Active",
        1: "Cancelled",
        2: "In Progress",
        3: "Success",
        4: "Failed",
        5: "Rejected",
        6: "Migrated/Unknown", // Ensure this matches the union type exactly
    };

    const { snipes, cancelSnipe, updateSnipe } = useSnipesStore();
    const [sellAmounts, setSellAmounts] = useState<{ [key: string]: string }>({});
    const [cancelingId, setCancelingId] = useState<string | null>(null);
    const [deletedSnipes, setDeletedSnipes] = useState<string[]>([]);
    const [revealedSnipes, setRevealedSnipes] = useState<boolean>(false);
    const [hasNewSnipes, setHasNewSnipes] = useState(false); // New state to track new snipes

    const memePad = useSniper((state) => state.memePad);

    // Load deleted snipes from local storage on component mount
    useEffect(() => {
        setDeletedSnipes(getDeletedSnipes());
    }, []);

    const handleSetSellAmount = (snipeId: string, value: string) => {
        setSellAmounts((prev) => ({ ...prev, [snipeId]: value }));
    };

    const handleCancelClick = async (snipeGuid: string) => {
        setCancelingId(snipeGuid);
        try {
            await cancelSnipe(snipeGuid);
        } finally {
            setCancelingId(null);
            showToast(`Snipe cancelled!`, "success");
        }
    };

    const handleUpdateSnipe = async (snipeGuid: string) => {
        try {
            await updateSnipe([snipeGuid]); // Always send as array
            showToast(`Snipe updated!`, "success");
        } catch (e) {
            showToast(`Failed to update snipe.`, "error");
        }
    };

    const handleSellClick = async (snipeGuid: string) => {
        // Add actual sell logic here using WalletController or similar
    };

    // Handle delete click - add snipe GUID to local storage and update state
    const handleDeleteClick = (snipeGuid: string) => {
        addDeletedSnipe(snipeGuid);
        setDeletedSnipes([...deletedSnipes, snipeGuid]);
        showToast(`Snipe removed from list`, "success");
    };

    // if (isLoading) {
    //     return (
    //         <Card className="flex justify-center items-center p-4 w-full min-w-full">
    //             <Loader />
    //         </Card>
    //     );
    // }

    if (!snipes || snipes.length === 0) {
        return <div className="card text-center text-gray-400 p-4">No active snipes found.</div>;
    }

    // Fetch mempad info for memePad snipes
    const {
        infoMap: mempadInfoMap,
        loadingMap: mempadLoadingMap,
        errorMap: mempadErrorMap,
    } = useMempadTokenInfoForSnipes(snipes);

    // Reveal new snipes after they are added to the list
    useEffect(() => {
        const hasUnrevealedSnipes = snipes.some((s) => s.revealed === false);
        setHasNewSnipes(hasUnrevealedSnipes); // Update the state based on unrevealed snipes
    }, [snipes]);

    useEffect(() => {
        if (!revealedSnipes && hasNewSnipes) {
            const timeoutId = setTimeout(() => {
                // Use zustand to update revealed for these snipes
                import("@/stores/snipes.store").then(({ useSnipesStore }) => {
                    useSnipesStore.setState((state: any) => ({
                        snipes: state.snipes.map((s: any) => (s.revealed === false ? { ...s, revealed: true } : s)),
                    }));
                });
                setRevealedSnipes(true);
            }, 100);

            return () => clearTimeout(timeoutId); // Cleanup timeout if component unmounts
        }
    }, [hasNewSnipes, revealedSnipes]);

    // State for toggling visibility of snipes by status
    const [hideActive, setHideActive] = useState(false);
    const [hideSniped, setHideSniped] = useState(false);
    const [hideInactive, setHideInactive] = useState(true);

    const handleToggleActive = () => setHideActive((prev) => !prev);
    const handleToggleSniped = () => setHideSniped((prev) => !prev);
    const handleToggleInactive = () => setHideInactive((prev) => !prev);

    // Helper to determine if a snipe should be hidden
    const isSnipeHidden = (status: SnipeStatus) => {
        if (status === "Active" && hideActive) return true;
        if (status === "Success" && hideSniped) return true;
        if (["Cancelled", "In Progress", "Failed", "Rejected", "Migrated/Unknown"].includes(status) && hideInactive)
            return true;
        return false;
    };

    // Helper to check if snipe is older than one day and status is not active or success
    const isOlderThanOneDayAndInactive = (createdAt: string | number | Date, status: string): boolean => {
        const now = new Date("2025-04-26T20:33:36+08:00"); // Use provided current time
        const created = new Date(createdAt);
        const diffMs = now.getTime() - created.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        return diffDays > 1 && status.toLowerCase() !== "active" && status.toLowerCase() !== "success";
    };

    return (
        <section
            className={cn(
                "gap-4 overflow-y-auto md:min-h-full md:h-auto w-full !scrollbar-hide rounded-[2.3rem] max-w-full relative",
                twoCol && "grid md:grid-cols-2 grid-cols-1 gap-4 auto-rows-auto",
                !twoCol && "flex flex-col items-center",
                className,
            )}
        >
            {showHeader && (
                <SnipeStatusTabs
                    onActive={handleToggleActive}
                    onSniped={handleToggleSniped}
                    onInactive={handleToggleInactive}
                    activeOn={!hideActive}
                    snipedOn={!hideSniped}
                    inactiveOn={!hideInactive}
                />
            )}
            {snipes
                .filter((snipe) => {
                    if (showMemes) {
                        // Only show memepad snipes when showMemes is true
                        return (
                            (snipe.mempadType === 1 || snipe.mempadType === 2) && !deletedSnipes.includes(snipe.guid)
                        );
                    }
                    // If showMemes is false, use zustand memePad switch
                    if (memePad) {
                        return (
                            (snipe.mempadType === 1 || snipe.mempadType === 2) && !deletedSnipes.includes(snipe.guid)
                        );
                    } else {
                        return (!snipe.mempadType || snipe.mempadType === 0) && !deletedSnipes.includes(snipe.guid);
                    }
                })
                .map((snipe) => {
                    const profitPercentage = 0;
                    const currentPrice = 0;
                    // If memePad snipe, get mempad info
                    let marketCap = "N/A";
                    let totalSupply = "N/A";
                    let priceInTon = "N/A";
                    const isMeme = snipe.mempadType === 1 || snipe.mempadType === 2;
                    const mempadInfo = isMeme ? mempadInfoMap[snipe.guid] : null;
                    const mempadLoading = isMeme ? mempadLoadingMap[snipe.guid] : false;
                    const mempadError = isMeme ? mempadErrorMap[snipe.guid] : null;
                    if (mempadInfo) {
                        totalSupply = mempadInfo.totalSupply;
                        priceInTon = mempadInfo.priceInTon;
                        marketCap = mempadInfo.marketCapInTon;
                    }

                    const age = "N/A";
                    const balance = 0;
                    const usdPrice = 0;
                    const profitClassName = profitPercentage >= 0 ? "text-green-500" : "text-red-500";
                    const currentSellAmount = sellAmounts[snipe.guid] || "";
                    const tokenName = snipe.assetOutInfo?.name || null;
                    const tokenSymbol = snipe.assetOutInfo?.symbol || "";
                    const tokenImage = snipe.assetOutInfo?.image || undefined;
                    const tokenAddress = snipe.assetOutInfo?.jettonMasterAddress || "";
                    const status = (sniperStatusMap[Number(snipe.status)] || "Migrated/Unknown") as SnipeStatus;
                    const sendingWallet = snipe.walletAddress || "";
                    const showSnipe = isMeme === !memePad;
                    // Use the helper to check if snipe is older than one day and inactive
                    const collapsed = isOlderThanOneDayAndInactive(snipe.createdAt, status);

                    return (
                        <Snipe
                            key={snipe.guid}
                            tokenName={tokenName}
                            tokenAddress={tokenAddress}
                            initialAmount={snipe.assetInAmount}
                            profitPercentage={profitPercentage}
                            profitClassName={profitClassName}
                            price={isMeme ? priceInTon : currentPrice}
                            walletAddress={sendingWallet}
                            watchingWallet={snipe.watchAddress}
                            marketCap={isMeme ? marketCap : marketCap}
                            totalSupply={isMeme ? totalSupply : undefined}
                            priceInTon={isMeme ? priceInTon : undefined}
                            age={age}
                            sendAmount={currentSellAmount}
                            setSendAmount={(value: string) => handleSetSellAmount(snipe.guid, value)}
                            balance={balance}
                            usdPrice={usdPrice}
                            onSellClick={
                                status === "Active"
                                    ? () => handleCancelClick(snipe.guid)
                                    : () => handleSellClick(snipe.guid)
                            }
                            isSellDisabled={
                                status === "Active"
                                    ? cancelingId === snipe.guid
                                    : !currentSellAmount || Number.parseFloat(currentSellAmount) <= 0
                            }
                            status={status}
                            assetOutInfo={snipe.assetOutInfo}
                            assetInInfo={snipe.assetInInfo}
                            tokenSymbol={tokenSymbol}
                            tokenImage={tokenImage}
                            revealed={snipe.revealed !== false}
                            updateSnipe={() => handleUpdateSnipe(snipe.guid)}
                            guid={snipe.guid}
                            hide={isSnipeHidden(status)}
                            memePad={isMeme}
                            collapsed={collapsed}
                            memePadType={snipe.mempadType}
                            mempadLoading={mempadLoading}
                            mempadError={mempadError}
                            onDeleteClick={
                                ["Cancelled", "Failed", "Rejected"].includes(status)
                                    ? () => handleDeleteClick(snipe.guid)
                                    : undefined
                            }
                        />
                    );
                })}
            <div className="hidden lg:flex flex-col w-full flex-1 h-full justify-end ">
                <div className="min-h-20 h-20 w-full min-w-full sticky bottom-0 bg-gradient-to-t from-background to-transparent"></div>
            </div>
        </section>
    );
};

export default SnipeList;
